import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { RowDiv } from "../../../../components/misc/misc.styled";
import { useFetchInsuranceCompanies } from "../../../../apolloClient/hooks/general/fetchHook";
import { InsuranceCompany, NewProviderInput } from "../../../../types/graphQlQuery.interface";
import { ADD_YOUR_NEW_PROVIDER } from "../../../../apolloClient/operation/mutations/mutation";

interface InsuranceProviderProps {
  handleRefetchProjectDetails: Function;
  insuranceData?: any;
}

export const InsuranceProvider: React.FC<InsuranceProviderProps> = ({
  handleRefetchProjectDetails,
  insuranceData
}) => {
    console.log("Insurance Data:", insuranceData);
  const { id } = useParams();
  const [keyword, setKeyword] = useState("");
  const [currentInsurance, setCurrentInsurance] = useState<InsuranceCompany | null>(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompany[]>([]);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

   const {
    insuranceCompanies: insuranceCompaniesDetails,
    loading,
    error,
    refetch,
  } = useFetchInsuranceCompanies({ limit: 100, offset: 0 });

  const [addYourProvider] = useMutation<
    { addYourProvider: boolean },
    { input: NewProviderInput }
  >(ADD_YOUR_NEW_PROVIDER);

  useEffect(() => {
    // Use real data from useFetchInsuranceCompanies
    if (!loading && insuranceCompaniesDetails) {
      setInsuranceCompanies(insuranceCompaniesDetails);
    }
    
    if (error) {
      console.error("Error fetching insurance companies:", error);
      toast.error("Failed to load insurance companies. Please try again.");
    }
  }, [insuranceCompaniesDetails, loading, error]);

  const getFilteredCompanies = () => {
    if (keyword.length > 0) {
      return insuranceCompanies.filter((item) =>
        item.companyName.toLowerCase().includes(keyword.toLowerCase())
      );
    }
    return insuranceCompanies;
  };

  const selectInsurance = async (insuranceId: number) => {
    try {
      const input: NewProviderInput = {
        providerId: insuranceId,
        projectId: Number(id),
      };

      const { data } = await addYourProvider({
        variables: { input },
      });

      if (data) {
        setConfirmationModalOpen(false);
        toast.success(
          insuranceData?.insuranceScope?.insuranceClaim?.providerId 
            ? "Provider updated successfully" 
            : "Provider added successfully"
        );
        handleRefetchProjectDetails();
      } else {
        toast.error(
          insuranceData?.insuranceScope?.insuranceClaim?.providerId 
            ? "Failed to update provider. Please try again." 
            : "Failed to add provider. Please try again."
        );
      }
    } catch (error) {
      console.error("Error adding provider:", error);
      toast.error(
        "An error occurred while adding the provider. Please try again later."
      );
    }
  };

  const handleProviderSelect = (company: InsuranceCompany) => {
    setCurrentInsurance(company);
    setConfirmationModalOpen(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <p>Loading insurance providers...</p>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          padding: "16px",
          backgroundColor: "#f8fafc",
          minHeight: "100%",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <div style={{ 
            display: "flex", 
            flexDirection: "column",
            gap: "24px",
          }}>
            {/* Selected Provider Section - Top on mobile */}
            <div>
              {insuranceData?.insuranceScope?.insuranceClaim?.providerId ? (
                <div>
                  <h3 style={{ 
                    textAlign: "center", 
                    marginBottom: "12px", 
                    color: "#1f2937", 
                    fontSize: "16px",
                    fontWeight: "600"
                  }}>
                    Selected Provider
                  </h3>
                  <div
                    style={{
                      border: "2px solid #4b87ed",
                      borderRadius: "8px",
                      padding: "16px",
                      textAlign: "center",
                      backgroundColor: "#fff",
                      boxShadow: "0px 2px 8px rgba(75, 135, 237, 0.12)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      maxWidth: "320px",
                      margin: "0 auto",
                    }}
                  >
                    {/* Find and display the selected provider logo */}
                    {(() => {
                      const selectedProvider = insuranceCompanies.find(
                        (company) => company.id === insuranceData.insuranceScope.insuranceClaim.providerId
                      );
                      const logoSrc = selectedProvider?.companyLogo;
                      
                      return logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={insuranceData.insuranceScope.insuranceClaim.companyName}
                          style={{
                            width: "64px",
                            height: "64px",
                            objectFit: "contain",
                            marginBottom: "12px",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "64px",
                            height: "64px",
                            backgroundColor: "#f5f5f5",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 12px auto",
                            fontSize: "32px",
                          }}
                        >
                          🏢
                        </div>
                      );
                    })()}
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "16px",
                        color: "#4b87ed",
                        fontWeight: "600",
                        lineHeight: "1.3",
                        textAlign: "center",
                      }}
                    >
                      {insuranceData.insuranceScope.insuranceClaim.companyName}
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "#dcfce7",
                        padding: "4px 10px",
                        borderRadius: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "6px",
                          height: "6px",
                          backgroundColor: "#22c55e",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <span style={{ fontSize: "12px", color: "#16a34a", fontWeight: "500" }}>
                        Selected
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 style={{ 
                    textAlign: "center", 
                    marginBottom: "12px", 
                    color: "#6b7280", 
                    fontSize: "16px",
                    fontWeight: "600"
                  }}>
                    No Provider Selected
                  </h3>
                  <div
                    style={{
                      border: "2px dashed #d1d5db",
                      borderRadius: "8px",
                      padding: "24px 16px",
                      textAlign: "center",
                      backgroundColor: "#fafafa",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      maxWidth: "320px",
                      margin: "0 auto",
                    }}
                  >
                    <div
                      style={{
                        width: "64px",
                        height: "64px",
                        backgroundColor: "#f3f4f6",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 12px auto",
                        fontSize: "32px",
                      }}
                    >
                      📋
                    </div>
                    <h4
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "14px",
                        color: "#6b7280",
                        fontWeight: "500",
                        lineHeight: "1.3",
                      }}
                    >
                      Select a provider from the list
                    </h4>
                    <p style={{ fontSize: "12px", color: "#9ca3af", margin: 0 }}>
                      Choose to proceed with your claim
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Side - Provider Selection */}
            <div style={{ flex: 1 }}>
              <h2 style={{ 
                textAlign: "left", 
                marginBottom: "6px", 
                color: "#1f2937", 
                fontSize: "20px",
                fontWeight: "600"
              }}>
                {insuranceData?.insuranceScope?.insuranceClaim?.providerId 
                  ? "Change Insurance Provider" 
                  : "Select Insurance Provider"}
              </h2>
              <p
                style={{
                  textAlign: "left",
                  marginBottom: "16px",
                  color: "#6b7280",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                {insuranceData?.insuranceScope?.insuranceClaim?.providerId 
                  ? "You can change your insurance provider if needed." 
                  : "Choose your insurance provider to proceed with your claim process."}
              </p>

              {/* Search Section */}
              <div style={{ marginBottom: "16px" }}>
                <input
                  type="text"
                  placeholder="Search providers..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  style={{
                    width: "100%",
                    maxWidth: "400px",
                    padding: "12px 14px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    outline: "none",
                    backgroundColor: "#f9fafb",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#4b87ed";
                    e.target.style.backgroundColor = "#fff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.backgroundColor = "#f9fafb";
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  flexWrap: "wrap",
                  gap: "16px",
                  maxHeight: "500px",
                  overflowY: "auto",
                  padding: "16px",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  position: "relative",
                }}
              >
                {getFilteredCompanies().length === 0 ? (
                  <div style={{ 
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    height: "100%",
                    minHeight: "300px"
                  }}>
                    <div style={{ textAlign: "center" }}>
                      <h4 style={{ color: "#6b7280", margin: "0 0 6px 0", fontSize: "16px", fontWeight: 600 }}>No insurance providers found</h4>
                      <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>
                        Try adjusting your search terms
                      </p>
                    </div>
                  </div>
                ) : (
                  getFilteredCompanies().map((company) => {
                    const isSelected = insuranceData?.insuranceScope?.insuranceClaim?.providerId === company.id;
                    return (
                      <div
                        key={company.id}
                        onClick={() => handleProviderSelect(company)}
                        style={{
                          border: isSelected ? "2px solid #4b87ed" : "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "16px",
                          width: "180px",
                          textAlign: "center",
                          backgroundColor: "#fff",
                          boxShadow: isSelected 
                            ? "0px 4px 12px rgba(75, 135, 237, 0.15)" 
                            : "0px 2px 6px rgba(0, 0, 0, 0.08)",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                          transform: isSelected ? "scale(1.02)" : "scale(1)",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.transform = "scale(1.02)";
                            e.currentTarget.style.boxShadow = "0px 4px 12px rgba(0, 0, 0, 0.12)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "0px 2px 6px rgba(0, 0, 0, 0.08)";
                          }
                        }}
                      >
                        {company.companyLogo ? (
                          <img
                            src={company.companyLogo}
                            alt={company.companyName}
                            style={{
                              width: "72px",
                              height: "72px",
                              objectFit: "contain",
                              marginBottom: "12px",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "72px",
                              height: "72px",
                              backgroundColor: "#f3f4f6",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              margin: "0 auto 12px auto",
                              fontSize: "36px",
                            }}
                          >
                            🏢
                          </div>
                        )}
                        <h4
                          style={{
                            margin: "0 0 6px 0",
                            fontSize: "14px",
                            color: isSelected ? "#4b87ed" : "#1f2937",
                            fontWeight: isSelected ? "600" : "500",
                            lineHeight: "1.3",
                          }}
                          className="text-wrap"
                        >
                          {company.companyName}
                        </h4>
                        {company.claimLink && (
                          <p style={{ 
                            fontSize: "11px", 
                            color: "#4b87ed", 
                            margin: "4px 0 0 0",
                            fontWeight: "500"
                          }}>
                            Claim Link Available
                          </p>
                        )}
                        {isSelected && (
                          <div
                            style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              backgroundColor: "#22c55e",
                              color: "white",
                              borderRadius: "50%",
                              width: "22px",
                              height: "22px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "13px",
                              fontWeight: "bold",
                              boxShadow: "0px 2px 4px rgba(34, 197, 94, 0.3)",
                            }}
                          >
                            ✓
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmationModalOpen && currentInsurance && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "20px",
              width: "90%",
              maxWidth: "400px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: "0 0 12px 0", color: "#1f2937", fontSize: "18px", fontWeight: 600 }}>
              {insuranceData?.insuranceScope?.insuranceClaim?.providerId 
                ? "Change Insurance Provider" 
                : "Confirm Insurance Provider Selection"}
            </h3>
            {currentInsurance.companyLogo ? (
              <img
                src={currentInsurance.companyLogo}
                alt={currentInsurance.companyName}
                style={{
                  width: "64px",
                  height: "64px",
                  objectFit: "contain",
                  marginBottom: "12px",
                }}
              />
            ) : (
              <div
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "#f3f4f6",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px auto",
                  fontSize: "32px",
                }}
              >
                🏢
              </div>
            )}
            <h4 style={{ margin: "8px 0", color: "#1f2937", fontSize: "16px", fontWeight: 600 }}>
              {currentInsurance.companyName}
            </h4>
            {currentInsurance.claimLink && (
              <div style={{ marginBottom: "12px" }}>
                <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
                  Claim filing will be available at:
                </p>
                <a href={currentInsurance.claimLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: "13px", color: "#4b87ed", wordBreak: "break-all" }}>
                  {currentInsurance.claimLink}
                </a>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "16px" }}>
              <button
                onClick={() => setConfirmationModalOpen(false)}
                style={{
                  padding: "10px 18px",
                  backgroundColor: "#f3f4f6",
                  color: "#6b7280",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => selectInsurance(currentInsurance.id)}
                style={{
                  padding: "10px 18px",
                  backgroundColor: "#4b87ed",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {insuranceData?.insuranceScope?.insuranceClaim?.providerId 
                  ? "Change Provider" 
                  : "Select Provider"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
